import { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import MessageComponent from 'workflow/common/MessageComponent'

import Button from 'common/components/button'

/**
 * Shown on embedded (iframe) routes when no Auth0 session could be obtained
 * silently. Interactive login cannot render inside an iframe (Auth0 blocks
 * framing), so the only viable path is a top-level popup, which must be
 * opened from a user gesture to avoid popup blockers.
 */
const EmbeddedSignIn = (): JSX.Element => {
  const { loginWithPopup } = useAuth0()
  const [isSigningIn, setIsSigningIn] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleSignIn = async (): Promise<void> => {
    setHasError(false)
    setIsSigningIn(true)
    try {
      await loginWithPopup()
    } catch {
      setHasError(true)
    } finally {
      setIsSigningIn(false)
    }
  }

  return (
    <MessageComponent
      isErrorStaticPage={false}
      textPrimary="Sign in to Jacquard"
      textSecondary={
        <span className="flex flex-col items-center gap-8">
          <span>
            Your session could not be verified. Sign in to continue — a new
            window will open.
          </span>
          {hasError && (
            <span data-testid="embedded-sign-in-error">
              We couldn&apos;t complete sign-in. Please allow pop-ups for this
              site and try again.
            </span>
          )}
          <Button
            variant="primary"
            loading={isSigningIn}
            data-testid="embedded-sign-in-button"
            onClick={handleSignIn}
          >
            Sign in
          </Button>
        </span>
      }
    />
  )
}

export default EmbeddedSignIn
